(function buildStudioModule(root, factory) {
  const advisor = typeof module === 'object' && module.exports
    ? require('./corkopia-build-advisor.js')
    : root.CorkopiaBuildAdvisor;
  const canvas = typeof module === 'object' && module.exports
    ? require('./corkopia-build-canvas.js')
    : root.CorkopiaBuildCanvas;
  const api = factory(root, advisor, canvas);
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root?.document) {
    root.CorkopiaBuildStudio = api;
    api.prepare(root.document);
  }
})(typeof window !== 'undefined' ? window : globalThis, (root, advisorApi, canvasApi) => {
  'use strict';

  const STORAGE_KEY = 'corkopia-build-studio-v1';
  const SEARCH_HANDOFF_KEY = 'corkopia-search-build-handoff-v1';
  const PROJECTS = {
    arboreal_fixture: 'Arboreal profile',
    terrestrial_fixture: 'Terrestrial profile',
    plant_fixture: 'Plant display profile',
    orchid_mount: 'Orchid Mount',
    crested_gecko: 'Legacy arboreal project',
    ball_python: 'Legacy terrestrial project',
    bearded_dragon: 'Legacy terrestrial project',
    dart_frogs: 'Legacy arboreal project',
    bioactive_display: 'Legacy plant project',
    tarantula: 'Legacy terrestrial project',
    isopods: 'Legacy terrestrial project',
    other: 'Other'
  };
  const STYLES = {
    dense_jungle: 'Dense Jungle',
    natural_forest: 'Natural Forest',
    open_climbing: 'Open Climbing',
    heavy_background: 'Heavy Background',
    display_piece: 'Display Piece',
    minimal: 'Minimal',
    mixed: 'Mixed',
    surprise_me: 'Surprise Me'
  };
  const BUDGETS = { 75: 'Under $75', 125: 'Around $125', 200: 'Around $200', unlimited: 'Unlimited' };
  const PREFERENCES = {
    mixed_cork: 'Mixed Cork',
    hollow_forms: 'Hollow Forms',
    flat_layers: 'Flat Layers',
    branching_forms: 'Branching Forms',
    statement_forms: 'Statement Forms',
    no_preference: 'No Preference'
  };
  const PRESETS = {
    '12x12x18': [12, 12, 18],
    '18x18x24': [18, 18, 24],
    '24x18x18': [24, 18, 18],
    '36x18x18': [36, 18, 18]
  };

  const createInitialState = () => ({
    version: 3,
    step: 1,
    project: '',
    enclosure: { preset: '', width: null, depth: null, height: null },
    style: '',
    preference: '',
    budget: '',
    decisions: [],
    shownVariantIds: [],
    activeVariantId: null,
    order: [],
    canvas: canvasApi?.createState?.() || {
      version: 'visual-build-canvas-v1',
      zoom: 1,
      activeLayer: 'all',
      selectedVariantId: null,
      placements: {}
    },
    requestedFunction: '',
    searchHandoffVariantId: null,
    emphasis: '',
    cursor: 0,
    savedAt: null
  });

  const finiteDimension = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed >= 4 && parsed <= 120 ? parsed : null;
  };

  const sanitizeState = (input = {}) => {
    const fallback = createInitialState();
    const enclosure = input.enclosure || {};
    const preset = Object.hasOwn(PRESETS, enclosure.preset) || enclosure.preset === 'custom' || enclosure.preset === 'none' ? enclosure.preset : '';
    const presetDimensions = PRESETS[preset] || [];
    return {
      ...fallback,
      step: Math.min(5, Math.max(1, Number(input.step) || 1)),
      project: Object.hasOwn(PROJECTS, input.project) ? input.project : '',
      enclosure: {
        preset,
        width: finiteDimension(presetDimensions[0] ?? enclosure.width),
        depth: finiteDimension(presetDimensions[1] ?? enclosure.depth),
        height: finiteDimension(presetDimensions[2] ?? enclosure.height)
      },
      style: Object.hasOwn(STYLES, input.style) ? input.style : '',
      preference: Object.hasOwn(PREFERENCES, input.preference) ? input.preference : '',
      budget: Object.hasOwn(BUDGETS, input.budget) ? String(input.budget) : '',
      decisions: Array.isArray(input.decisions)
        ? input.decisions.slice(-200).filter((entry) => entry && entry.variantId).map((entry) => ({
            action: String(entry.action || ''),
            variantId: Number(entry.variantId),
            pieceId: String(entry.pieceId || ''),
            family: String(entry.family || ''),
            functionId: String(entry.functionId || ''),
            emphasis: String(entry.emphasis || ''),
            at: entry.at || null
          }))
        : [],
      shownVariantIds: Array.isArray(input.shownVariantIds)
        ? [...new Set(input.shownVariantIds.map(Number).filter(Number.isFinite))].slice(-200)
        : [],
      activeVariantId: Number.isFinite(Number(input.activeVariantId)) ? Number(input.activeVariantId) : null,
      order: Array.isArray(input.order) ? [...new Set(input.order.map(Number).filter(Number.isFinite))] : [],
      canvas: canvasApi?.sanitizeState?.(input.canvas) || fallback.canvas,
      requestedFunction: String(input.requestedFunction || ''),
      searchHandoffVariantId: Number.isFinite(Number(input.searchHandoffVariantId))
        ? Number(input.searchHandoffVariantId)
        : null,
      emphasis: String(input.emphasis || ''),
      cursor: Math.max(0, Number(input.cursor) || 0),
      savedAt: input.savedAt || null
    };
  };

  const validateStep = (state, step) => {
    if (step === 1) return state.project ? '' : 'Choose what you are building.';
    if (step === 2) {
      if (!state.enclosure.preset) return 'Choose an enclosure size.';
      if (state.enclosure.preset === 'none') return '';
      if (![state.enclosure.width, state.enclosure.depth, state.enclosure.height].every(finiteDimension)) {
        return 'Enter width, depth, and height between 4 and 120 inches.';
      }
    }
    if (step === 3) {
      if (!state.style) return 'Choose a visual direction.';
      if (!state.preference) return 'Choose a material preference.';
    }
    if (step === 4) return state.budget ? '' : 'Choose a budget.';
    return '';
  };

  const cartPieces = (cart = {}) => (cart.items || []).filter((item) =>
    item.properties?._corkopia_inventory_type === 'individual_piece'
  );

  const pieceSubtotal = (cart = {}) => cartPieces(cart).reduce((sum, item) =>
    sum + (Number(item.final_line_price) || 0), 0
  );

  const budgetLimit = (state) => state.budget === 'unlimited' ? Infinity : Number(state.budget) * 100;

  const selectedBuild = (candidates, state, cart = {}) => cartPieces(cart).map((item) => {
    const variantId = Number(item.variant_id);
    const product = candidates.find((candidate) =>
      Number(candidate?.commerce?.selectedVariant?.id || candidate?.identity?.shopifyVariantId) === variantId
    );
    const accepted = [...state.decisions].reverse().find((decision) =>
      decision.action === 'keep' && Number(decision.variantId) === variantId
    );
    return {
      variantId,
      pieceId: product?.identity?.pieceId || item.properties?._corkopia_piece_id || '',
      family: product?.classification?.family || accepted?.family || item.properties?._corkopia_family || '',
      functionId: accepted?.functionId || '',
      priceCents: Number(item.final_line_price) || 0,
      weightOz: Number(item.properties?._corkopia_piece_weight_oz) || 0
    };
  });

  const advisorInput = (candidates, state, cart = {}, context = {}) => ({
    project: state.project,
    enclosure: {
      width: state.enclosure.width,
      depth: state.enclosure.depth,
      height: state.enclosure.height
    },
    style: state.style,
    preference: state.preference,
    budget: {
      currency: cart.currency || 'USD',
      merchandiseLimitCents: Number.isFinite(budgetLimit(state)) ? budgetLimit(state) : null,
      selectedSubtotalCents: pieceSubtotal(cart)
    },
    currentBuild: selectedBuild(candidates, state, cart),
    availableInventory: candidates,
    decisionHistory: state.decisions,
    shownVariantIds: state.shownVariantIds,
    requestedFunction: context.requestedFunction ?? state.requestedFunction,
    preferredVariantId: context.preferredVariantId ?? state.searchHandoffVariantId,
    emphasis: context.emphasis ?? state.emphasis,
    allowCurrentVariantId: context.allowCurrentVariantId || '',
    action: context.action || 'recommend',
    estimatedBox: cart.attributes?.corkopia_box_name || null,
    canvasState: state.canvas
  });

  const advisorResponse = (candidates, state, cart = {}, context = {}) =>
    advisorApi?.run?.(advisorInput(candidates, state, cart, context)) || {
      outcome: 'unavailable',
      recommendations: [],
      eligibility: [],
      explanation: { reasons: [], limitations: ['Build Advisor could not load.'] },
      stages: []
    };

  const eligibleCandidates = (candidates, state, cart = {}, context = {}) =>
    advisorResponse(candidates, state, cart, context).eligibility
      .filter((decision) => decision.eligible)
      .map((decision) => decision.subject);

  const nextCandidate = (candidates, state, cart = {}, context = {}) => {
    const response = advisorResponse(candidates, state, cart, context);
    const variantId = Number(response.recommendations?.[0]?.subject?.variant_id);
    return candidates.find((product) =>
      Number(product?.commerce?.selectedVariant?.id || product?.identity?.shopifyVariantId) === variantId
    ) || null;
  };

  const catalogRequest = (state, cart = {}) => ({
    contractVersion: 3,
    source: 'dynamic_inventory_service',
    inventoryContract: root.CorkopiaDynamicInventory?.contractFor?.('build_studio') || {
      contract_version: 'dynamic-inventory-v1.0.0',
      surface: 'build_studio',
      current_provider: 'shopify_public_sections',
      c3_provider_status: 'not_connected'
    },
    eligibility: 'currently_sellable_exact_pieces',
    advisor: 'deterministic_build_advisor_v1',
    ruleSet: 'build_advisor_v1',
    projectContext: state.project || null,
    enclosure: { ...state.enclosure },
    styleContext: state.style || null,
    preferenceContext: state.preference || null,
    budgetCents: Number.isFinite(budgetLimit(state)) ? budgetLimit(state) : null,
    excludeVariantIds: cartPieces(cart).map((item) => Number(item.variant_id)),
    publicFieldsOnly: true
  });

  const readStoredState = (storage) => {
    try {
      return sanitizeState(JSON.parse(storage?.getItem(STORAGE_KEY) || '{}'));
    } catch (_) {
      return createInitialState();
    }
  };

  const writeStoredState = (storage, state) => {
    try {
      storage?.setItem(STORAGE_KEY, JSON.stringify(sanitizeState(state)));
      return true;
    } catch (_) {
      return false;
    }
  };

  const functionForSearchHandoff = (handoff = {}) => {
    const intent = handoff.search_dna?.intent || {};
    const byRole = {
      climbing: 'climbing_feature',
      hide: 'primary_hide',
      mount: 'orchid_mount',
      background: 'background_coverage'
    };
    const byFamily = {
      cork_round: 'primary_hide',
      half_round: 'secondary_hide',
      cork_flat: 'background_coverage',
      branch: 'climbing_feature',
      background_piece: 'background_coverage',
      statement_piece: 'display_piece'
    };
    return byRole[intent.role] || byFamily[handoff.subject?.family] || '';
  };

  const consumeSearchHandoff = (storage, state, candidates = []) => {
    let handoff = null;
    try {
      handoff = JSON.parse(storage?.getItem(SEARCH_HANDOFF_KEY) || 'null');
      storage?.removeItem(SEARCH_HANDOFF_KEY);
    } catch (_) {
      return null;
    }
    if (
      handoff?.version !== 'search-build-handoff-v1'
      || handoff?.source !== 'corkopia_search'
      || handoff?.customer_confirmation_required !== true
      || handoff?.cart_mutation !== null
      || !handoff?.subject?.variant_id
    ) return null;
    const variantId = Number(handoff.subject.variant_id);
    const candidate = candidates.find((product) =>
      Number(product?.commerce?.selectedVariant?.id || product?.identity?.shopifyVariantId) === variantId
    );
    if (!candidate) return { ...handoff, accepted: false, reason: 'CURRENT_CANDIDATE_NOT_AVAILABLE' };
    state.searchHandoffVariantId = variantId;
    state.activeVariantId = variantId;
    state.requestedFunction = functionForSearchHandoff(handoff);
    if ([1, 2, 3, 4].every((step) => !validateStep(state, step))) state.step = 5;
    return { ...handoff, accepted: true, reason: 'CURRENT_CANDIDATE_QUEUED' };
  };

  const providers = Object.freeze({
    catalog: Object.freeze({
      id: 'dynamic_inventory_service_v1',
      load(scope) {
        const products = [...scope.querySelectorAll('[data-build-candidate-source]')]
          .map((host) => host.corkopiaProduct)
          .filter(Boolean);
        return root.CorkopiaDynamicInventory?.selectInventory
          ? root.CorkopiaDynamicInventory.selectInventory(products, {
              surface: 'build_studio',
              surface_id: scope.dataset.inventorySurfaceId || 'build-studio',
              exclusions: {}
            }).items
          : products;
      }
    }),
    sequence: Object.freeze({
      id: 'deterministic_build_advisor_v1',
      next: nextCandidate,
      request: advisorResponse
    }),
    persistence: Object.freeze({
      id: 'local_device_v1',
      load: readStoredState,
      save: writeStoredState
    }),
    future: Object.freeze({
      catalog: 'Replace the Dynamic Inventory Service Shopify adapter with an authenticated public C3 projection returning eligible LIVE products only.',
      intelligence: 'Move this deterministic contract behind the provider-neutral gateway when a server runtime is approved.',
      persistence: 'Replace local-device storage with consented account-backed save/share services.'
    })
  });

  let prepared = false;

  const prepare = (doc) => {
    if (prepared) return;
    prepared = true;
    root.CorkopiaBuildStudioProviders = providers;

    let scheduled = false;
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      root.setTimeout(() => {
        scheduled = false;
        doc.querySelectorAll('[data-build-studio]:not([data-build-studio-ready])').forEach((studio) => boot(studio));
      }, 0);
    };
    doc.addEventListener('corkopia:products-ready', schedule);
    doc.addEventListener('DOMContentLoaded', schedule);
    if (doc.readyState !== 'loading') schedule();
  };

  const boot = (studio) => {
    studio.dataset.buildStudioReady = 'true';
    const $ = (selector, scope = studio) => scope.querySelector(selector);
    const $$ = (selector, scope = studio) => [...scope.querySelectorAll(selector)];
    const modelApi = root.CorkopiaProductModel;
    const state = readStoredState(root.localStorage);
    const firstRunHandoff = root.CorkopiaStoreMemory?.consumeHandoff?.(root.sessionStorage);
    if (firstRunHandoff) {
      Object.assign(
        state,
        sanitizeState(root.CorkopiaStoreMemory.patchBuildState(
          state,
          firstRunHandoff,
          createInitialState
        ))
      );
      writeStoredState(root.localStorage, state);
    }
    let cart = { items: [], attributes: {} };
    let candidates = providers.catalog.load(studio);
    const searchHandoff = consumeSearchHandoff(root.sessionStorage, state, candidates);
    let current = null;
    let currentResponse = null;
    let currentRecommendation = null;
    let pendingRecommendation = null;
    let pendingVariantId = null;
    let statusTimer = null;
    let cartSyncTimer = null;
    let settingsWorkflowTimer = null;
    let arrivingVariantId = null;
    let canvasHistory = [];
    let canvasHistoryIndex = -1;

    const escapeHtml = (value = '') => String(value).replace(/[&<>"']/g, (character) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
    })[character]);
    const money = (cents = 0) => modelApi?.formatPrice
      ? modelApi.formatPrice(Number(cents) || 0, cart.currency || 'USD')
      : new Intl.NumberFormat(undefined, { style: 'currency', currency: cart.currency || 'USD' }).format((Number(cents) || 0) / 100);
    const formatMeasurement = (measurement) => modelApi?.formatMeasurement?.(measurement)
      || (measurement ? `${measurement.value} ${measurement.unit}` : '');
    const announce = (message) => {
      const region = $('[data-build-status]');
      if (!region) return;
      clearTimeout(statusTimer);
      region.textContent = message;
      statusTimer = root.setTimeout(() => { region.textContent = ''; }, 5000);
    };
    const persist = (explicit = false) => {
      state.savedAt = explicit ? new Date().toISOString() : state.savedAt;
      const saved = writeStoredState(root.localStorage, state);
      root.CorkopiaStoreMemory?.rememberBuild?.(root.localStorage, state);
      if (saved) {
        root.document.dispatchEvent(new CustomEvent('corkopia:build-context-updated', {
          detail: {
            version: state.version,
            step: state.step,
            decisionCount: state.decisions.length
          }
        }));
      }
      if (explicit) announce(saved ? 'Build progress saved on this device.' : 'This browser could not save build progress.');
    };
    const scheduleSettingsWorkflow = () => {
      clearTimeout(settingsWorkflowTimer);
      settingsWorkflowTimer = root.setTimeout(async () => {
        const orchestrator = root.CorkopiaOrchestrator;
        const orchestrationApi = root.CorkopiaEcosystemOrchestrator;
        if (!orchestrator || !orchestrationApi) return;
        const settings = {
          project: state.project,
          enclosure: { ...state.enclosure },
          style: state.style,
          preference: state.preference,
          budget: state.budget
        };
        const settingsRevision = [
          state.project,
          state.enclosure.width,
          state.enclosure.depth,
          state.enclosure.height,
          state.style,
          state.preference,
          state.budget
        ].join('-');
        const command = orchestrationApi.createCommand(
          orchestrationApi.COMMANDS.CHANGE_BUILD_SETTINGS,
          {
            settings,
            buildDnaVersion: `build-dna-v3-${settingsRevision}`,
            cartMutationAllowed: false
          },
          {
            sourceSystem: 'build_studio',
            actorType: 'anonymous_customer',
            causationId: settingsRevision
          }
        );
        const currentPieces = () => cartPieces(cart).map((item) => ({
          variantId: Number(item.variant_id),
          pieceId: itemPieceId(item)
        }));
        await orchestrator.execute(command, {
          versionBuildDna: () => ({ ok: true, schema: 'build-dna-v3', revision: settingsRevision }),
          identifyValidSelectedPieces: () => ({ ok: true, pieces: currentPieces() }),
          identifyPiecesNeedingReview: () => ({ ok: true, pieces: [], compatibilityAuthority: 'disabled_unapproved' }),
          recalculateBuildHealth: () => {
            renderCanvas();
            return { ok: true };
          },
          updateBuildTimeline: () => {
            renderCanvas();
            return { ok: true };
          },
          refreshAdvisors: () => {
            renderCandidate();
            renderAdvisorSummary(currentResponse);
            return { ok: true };
          },
          showBuildConflicts: () => {
            const limit = state.budget === 'unlimited' ? null : Number(state.budget) * 100;
            const subtotal = pieceSubtotal(cart);
            return {
              ok: true,
              conflicts: limit != null && subtotal > limit ? ['BUDGET_REVIEW_REQUIRED'] : [],
              cartPiecesPreserved: true
            };
          }
        });
      }, 90);
    };
    const requestReplacementWorkflow = async (payload, applyHistory) => {
      const orchestrator = root.CorkopiaOrchestrator;
      const orchestrationApi = root.CorkopiaEcosystemOrchestrator;
      if (!orchestrator || !orchestrationApi) {
        applyHistory();
        renderCandidate();
        return null;
      }
      const command = orchestrationApi.createCommand(
        orchestrationApi.COMMANDS.REQUEST_PRODUCT_REPLACEMENT,
        {
          recommendationId: payload.recommendationId,
          variantId: payload.variantId,
          pieceId: payload.pieceId,
          functionId: payload.functionId || '',
          decisionSequence: state.decisions.length + 1,
          cartMutationAllowed: false
        },
        {
          sourceSystem: payload.sourceSystem || 'build_studio',
          actorType: 'anonymous_customer',
          causationId: payload.recommendationId
        }
      );
      return orchestrator.execute(command, {
        recordReplacementHistory: () => {
          applyHistory();
          return { ok: true, cartMutation: false };
        },
        requestReplacementRecommendation: () => ({
          ok: true,
          functionId: payload.functionId || '',
          exclusions: {
            accepted: true,
            rejected: true,
            shown: true,
            unavailable: true
          }
        }),
        presentReplacement: () => {
          renderCandidate();
          return { ok: true, cartMutation: false };
        },
        emitAnalytics: () => ({ ok: true, provider: null })
      });
    };
    const getVariantId = (product) => Number(product?.commerce?.selectedVariant?.id || product?.identity?.shopifyVariantId);
    const itemPieceId = (item) => item.properties?._corkopia_piece_id || item.product_title || 'Exact piece';
    const labelForEnclosure = () => state.enclosure.width
      ? `${state.enclosure.width} × ${state.enclosure.depth} × ${state.enclosure.height} in`
      : 'Empty enclosure';
    const orderedPieces = () => {
      const pieces = cartPieces(cart);
      const positions = new Map(state.order.map((variantId, index) => [Number(variantId), index]));
      return [...pieces].sort((a, b) =>
        (positions.get(Number(a.variant_id)) ?? 999) - (positions.get(Number(b.variant_id)) ?? 999)
      );
    };
    const reconcileOrder = () => {
      const ids = cartPieces(cart).map((item) => Number(item.variant_id));
      state.order = state.order.filter((id) => ids.includes(Number(id)));
      ids.forEach((id) => { if (!state.order.includes(id)) state.order.push(id); });
    };
    const canvasPieces = () => orderedPieces().map((item) => {
      const variantId = Number(item.variant_id);
      const product = candidates.find((candidate) => getVariantId(candidate) === variantId);
      const accepted = [...state.decisions].reverse().find((decision) =>
        decision.action === 'keep' && Number(decision.variantId) === variantId
      );
      return {
        variantId,
        pieceId: product?.identity?.pieceId || item.properties?._corkopia_piece_id || '',
        title: product?.identity?.title || item.product_title || 'Exact cork piece',
        family: product?.classification?.family || accepted?.family || item.properties?._corkopia_family || '',
        functionId: accepted?.functionId || '',
        image: item.image || product?.media?.heroImage?.url || '',
        url: item.url || (product?.identity?.handle ? `/products/${product.identity.handle}` : ''),
        lineKey: item.key
      };
    });
    const reconcileCanvas = () => {
      state.canvas = canvasApi?.reconcile?.(state.canvas, canvasPieces(), state.decisions) || state.canvas;
      return state.canvas;
    };
    const recordCanvasHistory = () => {
      const snapshot = JSON.stringify(canvasApi?.sanitizeState?.(state.canvas) || state.canvas);
      if (canvasHistory[canvasHistoryIndex] === snapshot) return;
      canvasHistory = canvasHistory.slice(0, canvasHistoryIndex + 1);
      canvasHistory.push(snapshot);
      if (canvasHistory.length > 30) canvasHistory.shift();
      canvasHistoryIndex = canvasHistory.length - 1;
    };
    const resetCanvasHistory = () => {
      canvasHistory = [];
      canvasHistoryIndex = -1;
      recordCanvasHistory();
    };
    const restoreCanvasHistory = (offset) => {
      const nextIndex = canvasHistoryIndex + offset;
      if (nextIndex < 0 || nextIndex >= canvasHistory.length) return false;
      canvasHistoryIndex = nextIndex;
      state.canvas = canvasApi.sanitizeState(JSON.parse(canvasHistory[canvasHistoryIndex]));
      reconcileCanvas();
      return true;
    };

    const syncInputs = () => {
      $$('input[name="build_project"]').forEach((input) => { input.checked = input.value === state.project; });
      $$('input[name="build_enclosure"]').forEach((input) => { input.checked = input.value === state.enclosure.preset; });
      $$('input[name="build_style"]').forEach((input) => { input.checked = input.value === state.style; });
      $$('input[name="build_preference"]').forEach((input) => { input.checked = input.value === state.preference; });
      $$('input[name="build_budget"]').forEach((input) => { input.checked = input.value === state.budget; });
      $$('[data-build-dimension]').forEach((input) => {
        input.value = state.enclosure[input.dataset.buildDimension] ?? '';
      });
      const custom = state.enclosure.preset === 'custom';
      const customFields = $('[data-custom-dimensions]');
      if (customFields) customFields.hidden = !custom;
      $$('[data-build-dimension]').forEach((input) => { input.disabled = !custom; });
    };

    const renderProgress = () => {
      const labels = ['Project', 'Enclosure', 'Style', 'Budget', 'Build Studio'];
      const habitatChoices = [
        state.project,
        state.enclosure.width && state.enclosure.depth && state.enclosure.height,
        state.style && state.preference,
        state.budget
      ].filter(Boolean).length;
      const buildProgress = $('[data-build-progress]');
      const habitatProgress = $('[data-habitat-progress]');
      $('[data-build-progress-label]').textContent = labels[state.step - 1];
      buildProgress.setAttribute('aria-valuenow', String(state.step));
      buildProgress.style.setProperty('--studio-progress', String(state.step / 5));
      $('[data-habitat-progress-label]').textContent = `${habitatChoices} of 4 choices`;
      habitatProgress.setAttribute('aria-valuenow', String(habitatChoices));
      habitatProgress.style.setProperty('--studio-progress', String(habitatChoices / 4));
      const pieceCount = cartPieces(cart).length;
      $('[data-shopping-progress-label]').textContent = pieceCount
        ? `${pieceCount} exact ${pieceCount === 1 ? 'piece' : 'pieces'} selected`
        : 'Your box is empty';
      $$('[data-shopping-progress] i').forEach((pip, index) => pip.classList.toggle('is-filled', index < Math.min(pieceCount, 4)));
    };

    const renderBrief = () => {
      const brief = $('[data-build-brief]');
      if (!brief) return;
      const values = [
        [1, PROJECTS[state.project]],
        [2, labelForEnclosure()],
        [3, STYLES[state.style]],
        [3, PREFERENCES[state.preference]],
        [4, BUDGETS[state.budget]]
      ].filter((entry) => entry[1]);
      brief.innerHTML = values.map(([step, label]) =>
        `<button type="button" data-build-edit="${step}" aria-label="Edit ${escapeHtml(label)}">${escapeHtml(label)} ↗</button>`
      ).join('');
    };

    const renderCanvas = () => {
      const pieces = canvasPieces();
      const canvas = reconcileCanvas();
      const composition = canvasApi?.composition?.(canvas, pieces, state.decisions);
      const selected = pieces.find((piece) => String(piece.variantId) === String(canvas.selectedVariantId));
      const selectedPlacement = selected ? canvas.placements[String(selected.variantId)] : null;
      const activeLayer = canvas.activeLayer || 'all';
      $('[data-build-enclosure-label]').textContent = labelForEnclosure();
      $('[data-build-canvas-cart-state]').textContent = pieces.length
        ? `${pieces.length} Shopify cart ${pieces.length === 1 ? 'piece' : 'pieces'}`
        : 'Shopify cart is empty';
      $('[data-build-habitat-empty]').hidden = pieces.length > 0;
      $('[data-build-canvas-zoom-label]').textContent = `${Math.round(canvas.zoom * 100)}%`;
      $('[data-build-canvas-viewport]').style.setProperty('--canvas-zoom', String(canvas.zoom));

      $('[data-build-canvas-layers]').innerHTML = canvasApi.LAYERS.map((layer) => {
        const count = composition?.layerCounts?.[layer.id] || 0;
        const disabled = layer.status === 'future';
        return `<button
          type="button"
          data-build-canvas-layer="${escapeHtml(layer.id)}"
          aria-pressed="${activeLayer === layer.id}"
          ${disabled ? 'disabled aria-describedby="BuildCanvasFutureLayers"' : ''}
        ><span>${escapeHtml(layer.label)}</span>${disabled ? '<small>Future</small>' : `<small>${count}</small>`}</button>`;
      }).join('');

      $('[data-build-habitat-pieces]').innerHTML = pieces.map((piece, index) => {
        const placement = canvas.placements[String(piece.variantId)];
        const coordinates = canvasApi.placementCoordinates(placement, index);
        const isSelected = String(canvas.selectedVariantId) === String(piece.variantId);
        const isFiltered = activeLayer !== 'all' && placement.layer !== activeLayer;
        const functionLabel = advisorApi?.config?.functions?.[piece.functionId]?.label || canvasApi.humanize(placement.layer);
        return `<button
          type="button"
          class="build-studio__canvas-piece${isSelected ? ' is-selected' : ''}${placement.highlighted ? ' is-highlighted' : ''}${String(arrivingVariantId) === String(piece.variantId) ? ' is-arriving' : ''}${isFiltered ? ' is-filtered' : ''}"
          data-build-canvas-piece="${escapeHtml(piece.variantId)}"
          data-canvas-layer="${escapeHtml(placement.layer)}"
          data-canvas-region="${escapeHtml(placement.region)}"
          aria-pressed="${isSelected}"
          aria-label="${escapeHtml(`${piece.pieceId || piece.title}. ${functionLabel}. ${canvasApi.humanize(placement.region)}. Select for canvas actions.`)}"
          style="--piece-image:url('${escapeHtml(piece.image || '')}');--piece-x:${coordinates.x}%;--piece-y:${coordinates.y}%;--piece-rotation:${coordinates.rotation}deg;--piece-order:${index + 1}"
        ><span aria-hidden="true"></span><small>${escapeHtml(piece.pieceId || `Piece ${index + 1}`)}</small></button>`;
      }).join('');
      arrivingVariantId = null;

      const healthNode = $('[data-build-canvas-health]');
      healthNode.innerHTML = (composition?.health || []).map((entry) => `
        <article>
          <span aria-hidden="true">${entry.status.includes('improved') || entry.status.includes('represented') ? '✓' : '○'}</span>
          <div><strong>${escapeHtml(entry.status)}</strong><small>${escapeHtml(entry.evidence)}</small></div>
        </article>
      `).join('');

      const timelineNode = $('[data-build-canvas-timeline]');
      timelineNode.innerHTML = (composition?.timeline || []).map((entry) => `
        <li data-canvas-timeline-status="${escapeHtml(entry.status)}">
          <span aria-hidden="true">${entry.status === 'complete' ? '✓' : entry.status === 'current' ? '→' : '○'}</span>
          <div><strong>${escapeHtml(entry.label)}</strong><small>${escapeHtml(canvasApi.humanize(entry.status))}</small></div>
        </li>
      `).join('');

      const inspector = $('[data-build-canvas-inspector]');
      if (!selected || !selectedPlacement) {
        inspector.hidden = true;
      } else {
        inspector.hidden = false;
        inspector.innerHTML = `
          <div class="build-studio__canvas-inspector-heading">
            <img src="${escapeHtml(selected.image || '')}" alt="">
            <div><small>Selected piece</small><strong>${escapeHtml(selected.pieceId || selected.title)}</strong><span>${escapeHtml(canvasApi.humanize(selectedPlacement.layer))}</span></div>
            <button type="button" data-build-canvas-close aria-label="Close piece controls">×</button>
          </div>
          <fieldset class="build-studio__canvas-region-picker">
            <legend>Move to a logical region</legend>
            ${canvasApi.REGIONS.map((region) => `<button
              type="button"
              data-build-canvas-region="${escapeHtml(region)}"
              aria-pressed="${selectedPlacement.region === region}"
              aria-label="Move ${escapeHtml(selected.pieceId || selected.title)} to ${escapeHtml(canvasApi.humanize(region))}"
            ><span aria-hidden="true"></span><small>${escapeHtml(canvasApi.humanize(region))}</small></button>`).join('')}
          </fieldset>
          <label class="build-studio__canvas-layer-select">
            <span>Planning layer</span>
            <select data-build-canvas-layer-select aria-label="Planning layer for ${escapeHtml(selected.pieceId || selected.title)}">
              ${canvasApi.LAYERS.map((layer) => `<option value="${escapeHtml(layer.id)}" ${selectedPlacement.layer === layer.id ? 'selected' : ''} ${layer.status === 'future' ? 'disabled' : ''}>${escapeHtml(layer.label)}${layer.status === 'future' ? ' — future' : ''}</option>`).join('')}
            </select>
          </label>
          <div class="build-studio__canvas-piece-actions">
            <button type="button" data-build-canvas-highlight aria-pressed="${selectedPlacement.highlighted}">${selectedPlacement.highlighted ? 'Remove highlight' : 'Highlight'}</button>
            ${selected.url ? `<a href="${escapeHtml(selected.url)}">View Details</a>` : ''}
            <button type="button" data-build-replace="${escapeHtml(selected.lineKey)}" data-family="${escapeHtml(selected.family)}">Replace</button>
            <button type="button" data-build-remove="${escapeHtml(selected.lineKey)}">Remove</button>
          </div>
        `;
      }
      const undo = $('[data-build-canvas-undo]');
      const redo = $('[data-build-canvas-redo]');
      undo.disabled = canvasHistoryIndex <= 0;
      redo.disabled = canvasHistoryIndex < 0 || canvasHistoryIndex >= canvasHistory.length - 1;
      studio.canvasComposition = composition;
    };

    const renderSelected = () => {
      const pieces = orderedPieces();
      const families = [...new Set(pieces.map((item) => item.properties?._corkopia_family).filter(Boolean))];
      $('[data-build-piece-count]').textContent = String(pieces.length);
      $('[data-build-estimated-cost]').textContent = money(pieceSubtotal(cart));
      $('[data-build-current-box]').textContent = cart.attributes?.corkopia_box_name
        || (pieces.length ? 'Updating from your box' : 'Empty');
      $('[data-build-families]').textContent = families.length ? families.join(', ') : 'None yet';
      const selected = $('[data-build-selected]');
      if (!pieces.length) {
        selected.innerHTML = '<p class="build-studio__selected-empty">Kept pieces will appear here and in the real Shopify cart.</p>';
        return;
      }
      selected.innerHTML = pieces.map((item, index) => `
        <article class="build-studio__selected-line">
          <img src="${escapeHtml(item.image || '')}" alt="">
          <div>
            <strong>${escapeHtml(itemPieceId(item))}</strong>
            <small>${escapeHtml(item.properties?._corkopia_family || 'Natural cork')} · ${money(item.final_line_price)}</small>
            ${(() => {
              const accepted = [...state.decisions].reverse().find((decision) =>
                decision.action === 'keep' && Number(decision.variantId) === Number(item.variant_id)
              );
              const label = advisorApi?.config?.functions?.[accepted?.functionId]?.label;
              return label ? `<small>Function: ${escapeHtml(label)}</small>` : '';
            })()}
          </div>
          <div class="build-studio__selected-actions">
            <button type="button" data-build-move="${escapeHtml(item.key)}" data-direction="-1" ${index === 0 ? 'disabled' : ''}>Move up</button>
            <button type="button" data-build-move="${escapeHtml(item.key)}" data-direction="1" ${index === pieces.length - 1 ? 'disabled' : ''}>Move down</button>
            <button type="button" data-build-replace="${escapeHtml(item.key)}" data-family="${escapeHtml(item.properties?._corkopia_family || '')}">Replace</button>
            <button type="button" data-build-remove="${escapeHtml(item.key)}">Remove</button>
          </div>
        </article>
      `).join('');
    };

    const renderAdvisorSummary = (response) => {
      if (!response || response.outcome === 'invalid') return;
      const dna = canvasApi?.expandBuildDna?.(
        response.build_dna || {},
        state.canvas,
        canvasPieces(),
        state.decisions
      ) || response.build_dna || {};
      const score = response.build_score?.categories || {};
      const recommendation = response.recommendations?.[0];
      const estimatedWeight = dna.estimated_weight;
      const estimate = response.estimated_budget?.recommended_subtotal_cents
        ?? response.estimated_budget?.selected_subtotal_cents
        ?? pieceSubtotal(cart);

      $('[data-build-name]').textContent = response.build_name || 'Your Corkopia Build';
      $('[data-build-name-context]').textContent = `${PROJECTS[state.project]} · ${STYLES[state.style]} · ${PREFERENCES[state.preference]}`;
      $('[data-build-function]').textContent = recommendation?.function_label || 'No eligible function match';
      $('[data-build-advisor-estimate]').innerHTML = `${escapeHtml(money(estimate))}<small>Selected pieces${recommendation ? ' + current recommendation' : ''}</small>`;
      $('[data-build-weight]').innerHTML = estimatedWeight
        ? `${escapeHtml(Number(estimatedWeight.value).toFixed(1))} oz<small>Observed cart properties only</small>`
        : 'Not available<small>Observed cart properties only</small>';

      const scoreNode = $('[data-build-score]');
      scoreNode.innerHTML = Object.entries(score).map(([id, category]) => `
        <div>
          <span>${escapeHtml(id.replace(/_/g, ' '))}</span>
          <strong>${escapeHtml(category.label)}</strong>
          <small>${escapeHtml(category.explanation)}</small>
        </div>
      `).join('');

      const dnaNode = $('[data-build-dna]');
      const dnaRows = [
        ['Animal / project', PROJECTS[state.project]],
        ['Theme', STYLES[state.style]],
        ['Preference', PREFERENCES[state.preference]],
        ['Structure', String(dna.structure || '').replace(/_/g, ' ')],
        ['Chosen functions', (dna.chosen_functions || []).map((id) => advisorApi?.config?.functions?.[id]?.label || id).join(', ') || 'None yet'],
        ['Chosen layers', (dna.chosen_layers || []).map(canvasApi.humanize).join(', ') || 'None yet'],
        ['Placed pieces', String(dna.placements?.length || 0)],
        ['Chosen families', (dna.chosen_families || []).join(', ') || 'None yet'],
        ['Estimated box', dna.estimated_box || 'Calculated once you add a piece'],
        ['Estimated weight', estimatedWeight ? `${Number(estimatedWeight.value).toFixed(1)} oz` : 'Not available']
      ];
      dnaNode.innerHTML = dnaRows.map(([label, value]) =>
        `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`
      ).join('');
      studio.buildDNA = dna;
      response.build_dna = dna;
      studio.advisorResponse = response;
    };

    const candidateAddAttributes = (button, product, recommendation = null, response = null) => {
      const variantId = getVariantId(product);
      const dimensions = product.measurements || {};
      const packing = product.packing || {};
      const clearance = packing.protectiveClearance || {};
      const image = product.media?.heroImage || product.commerce?.selectedVariant?.image || {};
      const attrs = {
        variantId,
        productTitle: product.identity.title,
        productUrl: `/products/${product.identity.handle}`,
        pieceId: product.identity.pieceId,
        family: product.classification.familyLabel,
        weightValue: dimensions.weight?.value,
        weightUnit: dimensions.weight?.unit,
        minimumBox: packing.minimumBox,
        dimensions: [dimensions.maximumLength, dimensions.maximumWidth, dimensions.maximumHeight].map(formatMeasurement).join(' × '),
        lengthValue: dimensions.maximumLength?.value,
        lengthUnit: dimensions.maximumLength?.unit,
        widthValue: dimensions.maximumWidth?.value,
        widthUnit: dimensions.maximumWidth?.unit,
        heightValue: dimensions.maximumHeight?.value,
        heightUnit: dimensions.maximumHeight?.unit,
        volumeValue: packing.estimatedVolume?.value,
        volumeUnit: packing.estimatedVolume?.unit,
        packingFactor: packing.packingFactor,
        shapeClass: product.classification.family,
        orientation: packing.orientation,
        protectiveClearance: clearance.value,
        shippingBehavior: packing.shippingBehavior,
        priceCents: product.commerce.priceCents,
        image: image.url || image.previewUrl || ''
      };
      if (recommendation?.recommendation_id) attrs.recommendationId = recommendation.recommendation_id;
      if (response?.request_id) attrs.requestId = response.request_id;
      button.setAttribute('data-cork-add', '');
      Object.entries(attrs).forEach(([key, value]) => {
        if (value !== null && value !== undefined) button.dataset[key] = String(value);
      });
    };

    const animateRecommendationIntoCanvas = (product) => {
      if (!product || root.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
      const source = $('[data-build-candidate-card] .build-studio__candidate-media img');
      const target = $('[data-build-canvas-viewport]');
      if (!source || !target || typeof source.getBoundingClientRect !== 'function') return;
      const from = source.getBoundingClientRect();
      const to = target.getBoundingClientRect();
      if (!from.width || !to.width) return;
      const image = product.media?.heroImage || product.commerce?.selectedVariant?.image || {};
      const flyer = root.document.createElement('span');
      flyer.className = 'build-studio__canvas-flyer';
      flyer.setAttribute('aria-hidden', 'true');
      flyer.innerHTML = `<img src="${escapeHtml(image.url || image.previewUrl || '')}" alt="">`;
      Object.assign(flyer.style, {
        left: `${from.left + from.width / 2 - 34}px`,
        top: `${from.top + from.height / 2 - 34}px`
      });
      root.document.body.append(flyer);
      const deltaX = to.left + to.width / 2 - (from.left + from.width / 2);
      const deltaY = to.top + to.height / 2 - (from.top + from.height / 2);
      const animation = flyer.animate([
        { transform: 'translate3d(0,0,0) scale(1)', opacity: 1 },
        { transform: `translate3d(${deltaX * .58}px,${deltaY * .35 - 42}px,0) scale(.82)`, opacity: 1, offset: .55 },
        { transform: `translate3d(${deltaX}px,${deltaY}px,0) scale(.44)`, opacity: .2 }
      ], {
        duration: 680,
        easing: 'cubic-bezier(.2,.75,.22,1)',
        fill: 'forwards'
      });
      animation.finished.catch(() => {}).finally(() => flyer.remove());
    };

    const renderCandidate = () => {
      const container = $('[data-build-candidate]');
      const empty = $('[data-build-candidate-empty]');
      if (state.step !== 5) return;
      const allowedCurrentVariantId = current ? getVariantId(current) : state.activeVariantId || '';
      currentResponse = providers.sequence.request(candidates, state, cart, {
        allowCurrentVariantId: allowedCurrentVariantId,
        preferredVariantId: state.searchHandoffVariantId,
        action: allowedCurrentVariantId ? 'revalidate' : 'recommend'
      });
      currentRecommendation = currentResponse.recommendations?.[0] || null;
      const recommendedVariantId = Number(currentRecommendation?.subject?.variant_id);
      current = candidates.find((product) => getVariantId(product) === recommendedVariantId) || null;
      if (current && !state.shownVariantIds.includes(recommendedVariantId)) {
        state.shownVariantIds.push(recommendedVariantId);
        state.shownVariantIds = state.shownVariantIds.slice(-advisorApi.config.limits.maximumShownHistory);
      }
      state.activeVariantId = current ? recommendedVariantId : null;
      if (current && state.searchHandoffVariantId === recommendedVariantId) {
        studio.dataset.searchHandoffStatus = 'inserted';
      }
      renderAdvisorSummary(currentResponse);
      root.document.dispatchEvent(new CustomEvent('corkopia:build-advisor-result', {
        detail: {
          requestId: currentResponse.request_id,
          outcome: currentResponse.outcome,
          recommendationId: currentRecommendation?.recommendation_id || null,
          functionId: currentRecommendation?.function_id || null
        }
      }));
      if (!current) {
        container.hidden = true;
        empty.hidden = false;
        const underBudget = budgetLimit(state) - pieceSubtotal(cart);
        $('[data-build-candidate-empty-copy]').textContent = underBudget <= 0
          ? 'Your selected pieces have reached this budget. Change the budget, or review what is in your box.'
          : currentResponse.explanation?.summary || 'No other eligible exact piece remains for this build request.';
        return;
      }

      const dimensions = current.measurements || {};
      const image = current.media?.heroImage || current.commerce?.selectedVariant?.image || {};
      const explanations = currentResponse.explanation?.reasons || [];
      const limitations = currentResponse.explanation?.limitations || [];
      const alternatives = (currentRecommendation.replacement_options || []).map((reference) => {
        const product = candidates.find((candidate) => getVariantId(candidate) === Number(reference.variant_id));
        return product ? {
          title: product.identity.title,
          price: money(product.commerce.priceCents)
        } : null;
      }).filter(Boolean);
      const facts = [
        ['Function', currentRecommendation.function_label],
        ['Family', current.classification.familyLabel],
        ['Dimensions', [dimensions.maximumLength, dimensions.maximumWidth, dimensions.maximumHeight].map(formatMeasurement).join(' × ')],
        ['Weight', modelApi?.formatWeight?.(dimensions.weight) || formatMeasurement(dimensions.weight)],
        ['Opening', formatMeasurement(dimensions.openingDiameter || dimensions.openingWidth)]
      ].filter((entry) => entry[1]);
      container.hidden = false;
      empty.hidden = true;
      container.innerHTML = `
        <article class="build-studio__candidate-card" data-build-candidate-card>
          <div class="build-studio__candidate-media">
            <img src="${escapeHtml(image.url || image.previewUrl || '')}" alt="${escapeHtml(image.alt || current.identity.title)}" width="${Number(image.width) || 900}" height="${Number(image.height) || 1100}">
            <span>One of one · available now</span>
          </div>
          <div class="build-studio__candidate-copy">
            <div class="build-studio__candidate-title">
              <div><p>${escapeHtml(current.identity.pieceId)}</p><h3>${escapeHtml(current.identity.title)}</h3></div>
              <strong>${money(current.commerce.priceCents)}</strong>
            </div>
            <div class="build-studio__candidate-facts">
              ${facts.map(([label, value]) => `<span><small>${escapeHtml(label)}</small><strong>${escapeHtml(value)}</strong></span>`).join('')}
            </div>
            <div class="build-studio__advisor-reasons">
              <strong>Recommended because</strong>
              <ul>${explanations.map((reason) => `<li><span aria-hidden="true">✓</span>${escapeHtml(reason)}</li>`).join('')}</ul>
            </div>
            ${alternatives.length ? `
              <div class="build-studio__alternatives">
                <strong>Eligible alternatives</strong>
                <p>${alternatives.map((alternative) => `${escapeHtml(alternative.title)} · ${escapeHtml(alternative.price)}`).join('<br>')}</p>
              </div>
            ` : ''}
            <p class="build-studio__advisor-limitation">${escapeHtml(limitations[0] || '')}</p>
            <p class="build-studio__candidate-guarantee"><span aria-hidden="true">✓</span> Exact Piece Guarantee</p>
            <div class="build-studio__candidate-actions">
              <button class="c-button c-button--primary" type="button" data-build-keep>Keep · Add to Box</button>
              <button type="button" data-build-swap data-build-replace-candidate>Replace</button>
              <button type="button" data-build-skip>Skip</button>
              <a class="c-button c-button--text" data-build-view-details href="/products/${encodeURIComponent(current.identity.handle)}">View details</a>
            </div>
            <details class="build-studio__more-like">
              <summary>More Like This</summary>
              <div>
                ${Object.entries(advisorApi.config.moreLikeProfiles).map(([id, profile]) =>
                  `<button type="button" data-build-more-like="${escapeHtml(id)}">${escapeHtml(profile.label)}</button>`
                ).join('')}
              </div>
            </details>
            <div class="build-studio__pipeline-proof" aria-label="Recommendation pipeline">
              ${currentResponse.stages.map((stage) => `<span title="${escapeHtml(stage.codes.join(', '))}">${escapeHtml(stage.stage)}</span>`).join('')}
            </div>
          </div>
        </article>
      `;
      candidateAddAttributes($('[data-build-keep]', container), current, currentRecommendation, currentResponse);
    };

    const renderStep = ({ focus = false } = {}) => {
      $$('[data-build-step]').forEach((step) => {
        step.hidden = Number(step.dataset.buildStep) !== state.step;
      });
      syncInputs();
      renderProgress();
      renderBrief();
      renderCanvas();
      renderSelected();
      renderCandidate();
      persist(false);
      if (focus) {
        const heading = $(`[data-build-step="${state.step}"] h2`);
        heading?.setAttribute('tabindex', '-1');
        heading?.focus({ preventScroll: true });
        heading?.scrollIntoView({ block: 'start', behavior: root.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }
    };

    const setStep = (step, focus = true) => {
      state.step = Math.min(5, Math.max(1, Number(step)));
      current = null;
      currentResponse = null;
      currentRecommendation = null;
      renderStep({ focus });
    };

    const commitCanvasChange = (nextState, message) => {
      state.canvas = canvasApi.sanitizeState(nextState);
      reconcileCanvas();
      recordCanvasHistory();
      renderCanvas();
      renderAdvisorSummary(currentResponse);
      persist(false);
      if (message) announce(message);
      root.document.dispatchEvent(new CustomEvent('corkopia:build-canvas-updated', {
        detail: {
          version: canvasApi.VERSION,
          pieceCount: canvasPieces().length,
          selectedVariantId: state.canvas.selectedVariantId
        }
      }));
    };

    const reenterAdvisor = (action, context = {}) => {
      if (!current) return null;
      const response = providers.sequence.request(candidates, state, cart, {
        action,
        allowCurrentVariantId: getVariantId(current),
        requestedFunction: context.requestedFunction ?? currentRecommendation?.function_id,
        emphasis: context.emphasis ?? state.emphasis
      });
      const sameCandidate = Number(response.recommendations?.[0]?.subject?.variant_id) === getVariantId(current);
      return sameCandidate ? response : null;
    };

    const setDecision = (action, context = {}) => {
      if (!current || !currentRecommendation) return;
      const actionResponse = reenterAdvisor(action, {
        ...context,
        requestedFunction: currentRecommendation.function_id
      });
      if (!actionResponse) {
        current = null;
        currentRecommendation = null;
        announce('That exact piece is no longer eligible. The advisor is checking current inventory.');
        renderCandidate();
        return;
      }
      const functionId = currentRecommendation.function_id;
      state.decisions.push({
        action,
        variantId: getVariantId(current),
        pieceId: current.identity.pieceId,
        family: current.classification.family,
        functionId,
        emphasis: context.emphasis || '',
        at: new Date().toISOString()
      });
      state.cursor += 1;
      state.requestedFunction = context.requestedFunction || functionId;
      state.searchHandoffVariantId = null;
      state.emphasis = action === 'more_like' ? context.emphasis || '' : '';
      state.activeVariantId = null;
      current = null;
      currentResponse = null;
      currentRecommendation = null;
      renderCandidate();
      persist(false);
      announce(action === 'more_like'
        ? `The advisor applied ${advisorApi.config.moreLikeProfiles[context.emphasis]?.label || 'your emphasis'} and checked eligibility again.`
        : `The advisor checked another eligible ${advisorApi.config.functions[functionId]?.label || 'piece'}.`);
    };

    const syncCart = async () => {
      try {
        const previousCanvasMembership = canvasPieces().map((piece) => String(piece.variantId)).join('|');
        const response = await root.fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!response.ok) return;
        cart = await response.json();
        reconcileOrder();
        reconcileCanvas();
        const nextCanvasMembership = canvasPieces().map((piece) => String(piece.variantId)).join('|');
        if (previousCanvasMembership !== nextCanvasMembership) resetCanvasHistory();
        else if (!canvasHistory.length) recordCanvasHistory();
        renderProgress();
        renderBrief();
        renderCanvas();
        renderSelected();
        if (!pendingVariantId) renderCandidate();
        persist(false);
      } catch (_) {
        announce('Your box could not refresh. Nothing in your cart was changed.');
      }
    };

    const scheduleCartSync = () => {
      clearTimeout(cartSyncTimer);
      cartSyncTimer = root.setTimeout(syncCart, 30);
    };
    const refreshCatalog = () => {
      candidates = providers.catalog.load(studio);
      studio.dataset.buildCandidateCount = String(candidates.length);
      if (current && !candidates.includes(current)) current = null;
      renderCandidate();
    };

    studio.addEventListener('change', (event) => {
      const input = event.target;
      const canvasLayer = input.closest?.('[data-build-canvas-layer-select]');
      if (canvasLayer && state.canvas.selectedVariantId) {
        commitCanvasChange(
          canvasApi.updatePlacement(state.canvas, state.canvas.selectedVariantId, { layer: canvasLayer.value }),
          `${canvasApi.humanize(canvasLayer.value)} layer selected. Shopify cart contents are unchanged.`
        );
        return;
      }
      if (input.name === 'build_project') state.project = input.value;
      if (input.name === 'build_enclosure') {
        state.enclosure.preset = input.value;
        const dimensions = PRESETS[input.value];
        if (dimensions) [state.enclosure.width, state.enclosure.depth, state.enclosure.height] = dimensions;
        if (input.value === 'custom') {
          state.enclosure.width = finiteDimension($('[data-build-dimension="width"]').value);
          state.enclosure.depth = finiteDimension($('[data-build-dimension="depth"]').value);
          state.enclosure.height = finiteDimension($('[data-build-dimension="height"]').value);
        }
        if (input.value === 'none') {
          state.enclosure.width = null;
          state.enclosure.depth = null;
          state.enclosure.height = null;
        }
      }
      if (input.name === 'build_style') state.style = input.value;
      if (input.name === 'build_preference') state.preference = input.value;
      if (input.name === 'build_budget') state.budget = input.value;
      syncInputs();
      renderProgress();
      persist(false);
      scheduleSettingsWorkflow();
    });

    studio.addEventListener('input', (event) => {
      const dimension = event.target.closest('[data-build-dimension]');
      if (!dimension) return;
      state.enclosure[dimension.dataset.buildDimension] = finiteDimension(dimension.value);
      renderProgress();
      persist(false);
      scheduleSettingsWorkflow();
    });

    studio.addEventListener('click', async (event) => {
      const entry = event.target.closest('[data-build-entry]');
      if (entry) {
        const mode = entry.dataset.buildEntry;
        if (mode === 'resume') {
          setStep(state.step || 1);
          return;
        }
        if (mode === 'profile') {
          setStep(1);
          return;
        }
        if (!state.project) state.project = 'other';
        if (mode === 'enclosure') {
          setStep(2);
          return;
        }
        if (mode === 'cork' || mode === 'no_enclosure') {
          state.enclosure = { preset: 'none', width: null, depth: null, height: null };
          setStep(3);
          return;
        }
        if (mode === 'empty') {
          state.enclosure = { preset: 'none', width: null, depth: null, height: null };
          state.style = 'mixed';
          state.preference = 'no_preference';
          state.budget = 'unlimited';
          setStep(5);
          return;
        }
      }
      const canvasPiece = event.target.closest('[data-build-canvas-piece]');
      if (canvasPiece) {
        state.canvas = canvasApi.sanitizeState({
          ...state.canvas,
          selectedVariantId: canvasPiece.dataset.buildCanvasPiece
        });
        renderCanvas();
        persist(false);
        return;
      }
      if (event.target.closest('[data-build-canvas-close]')) {
        state.canvas = canvasApi.sanitizeState({ ...state.canvas, selectedVariantId: null });
        renderCanvas();
        persist(false);
        return;
      }
      const canvasRegion = event.target.closest('[data-build-canvas-region]');
      if (canvasRegion && state.canvas.selectedVariantId) {
        commitCanvasChange(
          canvasApi.updatePlacement(state.canvas, state.canvas.selectedVariantId, {
            region: canvasRegion.dataset.buildCanvasRegion
          }),
          `Piece moved to ${canvasApi.humanize(canvasRegion.dataset.buildCanvasRegion)}. This is planning placement only.`
        );
        return;
      }
      const canvasLayer = event.target.closest('[data-build-canvas-layer]');
      if (canvasLayer && !canvasLayer.disabled) {
        state.canvas = canvasApi.sanitizeState({
          ...state.canvas,
          activeLayer: state.canvas.activeLayer === canvasLayer.dataset.buildCanvasLayer
            ? 'all'
            : canvasLayer.dataset.buildCanvasLayer
        });
        renderCanvas();
        persist(false);
        return;
      }
      if (event.target.closest('[data-build-canvas-highlight]') && state.canvas.selectedVariantId) {
        const placement = state.canvas.placements[state.canvas.selectedVariantId];
        commitCanvasChange(
          canvasApi.updatePlacement(state.canvas, state.canvas.selectedVariantId, {
            highlighted: !placement?.highlighted
          }),
          placement?.highlighted ? 'Piece highlight removed.' : 'Piece highlighted on the planning canvas.'
        );
        return;
      }
      const zoom = event.target.closest('[data-build-canvas-zoom]');
      if (zoom) {
        const currentIndex = canvasApi.ZOOM_LEVELS.indexOf(state.canvas.zoom);
        const requested = zoom.dataset.buildCanvasZoom;
        const nextZoom = requested === 'reset'
          ? 1
          : canvasApi.ZOOM_LEVELS[Math.max(0, Math.min(
              canvasApi.ZOOM_LEVELS.length - 1,
              currentIndex + Number(requested)
            ))];
        state.canvas = canvasApi.sanitizeState({ ...state.canvas, zoom: nextZoom });
        renderCanvas();
        persist(false);
        announce(`Canvas view set to ${Math.round(nextZoom * 100)} percent.`);
        return;
      }
      if (event.target.closest('[data-build-canvas-reset-view]')) {
        state.canvas = canvasApi.sanitizeState({
          ...state.canvas,
          zoom: 1,
          activeLayer: 'all',
          selectedVariantId: null
        });
        renderCanvas();
        persist(false);
        announce('Canvas view reset. Piece placement and Shopify cart contents are unchanged.');
        return;
      }
      if (event.target.closest('[data-build-canvas-undo]')) {
        if (restoreCanvasHistory(-1)) {
          renderCanvas();
          renderAdvisorSummary(currentResponse);
          persist(false);
          announce('Canvas placement undone. Shopify cart contents are unchanged.');
        }
        return;
      }
      if (event.target.closest('[data-build-canvas-redo]')) {
        if (restoreCanvasHistory(1)) {
          renderCanvas();
          renderAdvisorSummary(currentResponse);
          persist(false);
          announce('Canvas placement restored. Shopify cart contents are unchanged.');
        }
        return;
      }
      const next = event.target.closest('[data-build-next]');
      if (next) {
        const error = validateStep(state, state.step);
        const errorNode = $('[data-build-step-error]', next.closest('[data-build-step]'));
        if (error) {
          if (errorNode) { errorNode.hidden = false; errorNode.textContent = error; }
          announce(error);
          return;
        }
        if (errorNode) errorNode.hidden = true;
        setStep(state.step + 1);
        return;
      }
      if (event.target.closest('[data-build-back]')) {
        setStep(state.step - 1);
        return;
      }
      const edit = event.target.closest('[data-build-edit]');
      if (edit) {
        setStep(edit.dataset.buildEdit);
        return;
      }
      if (event.target.closest('[data-build-swap]')) {
        const recommendation = currentRecommendation;
        const product = current;
        await requestReplacementWorkflow({
          recommendationId: recommendation?.recommendation_id || `recommendation-${getVariantId(product)}`,
          variantId: getVariantId(product),
          pieceId: product?.identity?.pieceId || `piece-${getVariantId(product)}`,
          functionId: recommendation?.function_id || ''
        }, () => setDecision('replace'));
        return;
      }
      if (event.target.closest('[data-build-skip]')) {
        setDecision('skip');
        return;
      }
      const moreLike = event.target.closest('[data-build-more-like]');
      if (moreLike) {
        const emphasis = moreLike.dataset.buildMoreLike;
        setDecision('more_like', {
          emphasis,
          requestedFunction: advisorApi.config.moreLikeProfiles[emphasis]?.functionId || ''
        });
        return;
      }
      if (event.target.closest('[data-build-view-details]')) {
        const response = reenterAdvisor('view_details');
        root.document.dispatchEvent(new CustomEvent('corkopia:build-advisor-action', {
          detail: {
            action: 'view_details',
            requestId: response?.request_id || null,
            recommendationId: currentRecommendation?.recommendation_id || null
          }
        }));
      }
      const keep = event.target.closest('[data-build-keep]');
      if (keep && current) {
        const response = reenterAdvisor('keep');
        if (!response) {
          event.preventDefault();
          current = null;
          currentRecommendation = null;
          announce('Shopify availability changed. This piece was not added.');
          renderCandidate();
          return;
        }
        pendingVariantId = getVariantId(current);
        pendingRecommendation = currentRecommendation;
        announce(`Adding ${current.identity.pieceId} through Shopify…`);
        return;
      }
      const remove = event.target.closest('[data-build-remove]');
      if (remove) {
        remove.disabled = true;
        await root.CorkopiaMyBox?.removeLine?.(remove.dataset.buildRemove, remove);
        return;
      }
      const replace = event.target.closest('[data-build-replace]');
      if (replace) {
        replace.disabled = true;
        const replacedItem = cartPieces(cart).find((item) => item.key === replace.dataset.buildReplace);
        const replacedDecision = [...state.decisions].reverse().find((decision) =>
          decision.action === 'keep' && Number(decision.variantId) === Number(replacedItem?.variant_id)
        );
        if (replacedItem) {
          await requestReplacementWorkflow({
            recommendationId: `selected-${Number(replacedItem.variant_id)}`,
            variantId: Number(replacedItem.variant_id),
            pieceId: itemPieceId(replacedItem),
            functionId: replacedDecision?.functionId || '',
            sourceSystem: replace.closest('[data-build-canvas-inspector]') ? 'visual_build_canvas' : 'build_studio'
          }, () => {
            state.decisions.push({
              action: 'replace',
              variantId: Number(replacedItem.variant_id),
              pieceId: itemPieceId(replacedItem),
              family: replacedDecision?.family || replacedItem.properties?._corkopia_family || '',
              functionId: replacedDecision?.functionId || '',
              emphasis: '',
              at: new Date().toISOString()
            });
            state.requestedFunction = replacedDecision?.functionId || '';
            state.emphasis = '';
            state.activeVariantId = null;
            current = null;
            currentResponse = null;
            currentRecommendation = null;
            state.cursor += 1;
            persist(false);
          });
          announce('A replacement is ready to review. Your current piece stays in your box until you choose otherwise.');
        }
        replace.disabled = false;
        return;
      }
      const move = event.target.closest('[data-build-move]');
      if (move) {
        const pieces = orderedPieces();
        const currentIndex = pieces.findIndex((item) => item.key === move.dataset.buildMove);
        const targetIndex = currentIndex + Number(move.dataset.direction);
        if (currentIndex < 0 || targetIndex < 0 || targetIndex >= pieces.length) return;
        const first = Number(pieces[currentIndex].variant_id);
        const second = Number(pieces[targetIndex].variant_id);
        const firstIndex = state.order.indexOf(first);
        const secondIndex = state.order.indexOf(second);
        [state.order[firstIndex], state.order[secondIndex]] = [state.order[secondIndex], state.order[firstIndex]];
        renderCanvas();
        renderSelected();
        persist(false);
        announce('Planning order updated. Shopify cart contents are unchanged.');
        return;
      }
      if (event.target.closest('[data-build-save]')) persist(true);
    });

    studio.addEventListener('keydown', (event) => {
      const piece = event.target.closest?.('[data-build-canvas-piece]');
      if (!piece || !['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      const direction = event.key.replace('Arrow', '').toLowerCase();
      const variantId = piece.dataset.buildCanvasPiece;
      const placement = state.canvas.placements[String(variantId)];
      if (!placement) return;
      const region = canvasApi.moveRegion(placement.region, direction);
      commitCanvasChange(
        canvasApi.updatePlacement(state.canvas, variantId, { region }),
        `Piece moved to ${canvasApi.humanize(region)}.`
      );
      root.requestAnimationFrame(() =>
        $(`[data-build-canvas-piece="${variantId}"]`)?.focus()
      );
    });

    root.document.addEventListener('corkopia:piece-added', (event) => {
      const variantId = Number(event.detail?.variantId);
      if (pendingVariantId && variantId === pendingVariantId) {
        const accepted = current;
        const acceptedRecommendation = pendingRecommendation || currentRecommendation;
        arrivingVariantId = variantId;
        animateRecommendationIntoCanvas(accepted);
        state.decisions.push({
          action: 'keep',
          variantId,
          pieceId: accepted?.identity?.pieceId || event.detail?.pieceId || '',
          family: accepted?.classification?.family || '',
          functionId: acceptedRecommendation?.function_id || '',
          emphasis: state.emphasis || '',
          at: new Date().toISOString()
        });
        if (!state.order.includes(variantId)) state.order.push(variantId);
        const card = $('[data-build-candidate-card]');
        card?.classList.add('is-locking');
        pendingVariantId = null;
        pendingRecommendation = null;
        state.requestedFunction = '';
        state.searchHandoffVariantId = null;
        state.emphasis = '';
        state.activeVariantId = null;
        state.cursor += 1;
        root.setTimeout(() => {
          current = null;
          syncCart().then(() => {
            reconcileCanvas();
            recordCanvasHistory();
            renderCanvas();
          });
        }, root.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 10 : 460);
      } else {
        scheduleCartSync();
      }
    });

    root.document.addEventListener('corkopia:piece-add-failed', (event) => {
      if (Number(event.detail?.variantId) === pendingVariantId) {
        pendingVariantId = null;
        pendingRecommendation = null;
        announce(event.detail?.message || 'Shopify could not add this exact piece.');
        renderCandidate();
      }
    });
    root.document.addEventListener('corkopia:piece-removed', scheduleCartSync);
    root.document.addEventListener('corkopia:cart-updated', scheduleCartSync);
    root.document.addEventListener('corkopia:products-ready', refreshCatalog);
    root.document.addEventListener('corkopia:orchestrator-step-requested', (event) => {
      const name = event.detail?.name;
      if (name === 'CANVAS_SYNCHRONIZED' || name === 'BUILD_DNA_UPDATED') scheduleCartSync();
      if (name === 'BUILD_HEALTH_RECALCULATED' || name === 'BUILD_TIMELINE_UPDATED') renderCanvas();
      if (name === 'ADVISORS_REFRESHED' || name === 'NEXT_BEST_PIECE_REFRESHED') refreshCatalog();
    });

    refreshCatalog();
    renderStep({ focus: false });
    syncCart();
    root.setTimeout(refreshCatalog, 60);

    root.document.dispatchEvent(new CustomEvent('corkopia:build-studio-ready', {
      detail: {
        catalogProvider: providers.catalog.id,
        sequenceProvider: providers.sequence.id,
        candidateCount: candidates.length,
        request: catalogRequest(state, cart),
        searchHandoff: searchHandoff
          ? { accepted: searchHandoff.accepted, reason: searchHandoff.reason }
          : null
      }
    }));
  };

  return {
    STORAGE_KEY,
    SEARCH_HANDOFF_KEY,
    createInitialState,
    sanitizeState,
    validateStep,
    cartPieces,
    pieceSubtotal,
    selectedBuild,
    consumeSearchHandoff,
    functionForSearchHandoff,
    advisorInput,
    advisorResponse,
    eligibleCandidates,
    nextCandidate,
    catalogRequest,
    providers,
    prepare
  };
});
